<template>
  <div class="weekly-projection-analysis-requirement pa-4">
    <!-- 1. Encabezado Neutro Limpio -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-avatar color="grey lighten-4" size="48" class="mr-4">
          <v-icon color="teal darken-2" large>mdi-flask-round-bottom-outline</v-icon>
        </v-avatar>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Requerimiento de Análisis por Proyección Semanal
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Aprobación, revisión y cancelación del requerimiento de análisis de materia prima sobre las proyecciones de cosecha semanales.
          </span>
        </div>
      </v-card-title>
    </v-card>

    <!-- 2. Tarjeta de Filtros de Búsqueda -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros de Búsqueda
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="3">
            <s-select-campaign-area
              label="Campaña"
              v-model="filters.CmaID"
              typeArea="1"
              clearable
              noDefault
              full
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <s-select
              label="Año"
              :items="years"
              outlined
              hide-details
              dense
              v-model="filters.WptYear"
              clearable
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <s-periods
              label="Semana"
              v-model="selectedWeekObj"
              :value-id="filters.PdsID"
              :year="filters.WptYear"
              :type="1"
              return-object
              clearable
              @input="onWeekChange"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <s-select-definition
              label="Cultivo"
              :def="1173"
              v-model="filters.TypeCultive"
              clearable
            />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <s-select-variety
              :cultiveID="filters.TypeCultive"
              label="Variedad"
              full
              clearable
              v-model="filters.VrtID"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <s-select-lot
              label="Sub Lote"
              v-model="filters.FltID"
              clearable
              full
              noDefault
            />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <s-select
              label="Filtro de Requerimiento"
              :items="filterStatusOptions"
              v-model="localStatusFilter"
              outlined
              dense
              hide-details
            />
          </v-col>

          <v-col cols="12" md="6" class="text-right d-flex align-center justify-end mt-2 mt-md-0">
            <v-btn
              color="primary"
              class="mr-2 px-4 rounded-lg font-weight-bold white--text text-none"
              elevation="0"
              height="38"
              :loading="loading"
              @click="loadData"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Consultar
            </v-btn>

            <v-btn
              color="success"
              class="px-4 rounded-lg font-weight-bold white--text text-none"
              elevation="0"
              height="38"
              :disabled="filteredList.length === 0"
              @click="exportToExcel"
            >
              <v-icon left small>mdi-file-excel</v-icon>
              Exportar Excel
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 3. Strip de KPIs Neutro (Sin degradados, fondos limpios) -->
    <v-row dense class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="grey lighten-4" size="42" class="mr-3">
              <v-icon color="grey darken-3">mdi-clipboard-list-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Total Proyecciones
              </div>
              <div class="text-h6 font-weight-black grey--text text--darken-4">
                {{ projectionList.length }}
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="grey lighten-4" size="42" class="mr-3">
              <v-icon color="grey darken-3">mdi-archive-arrow-down-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Jabas Semanales
              </div>
              <div class="text-h6 font-weight-black grey--text text--darken-4">
                {{ totalCantidadSemana | formatNumber }}
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="teal lighten-5" size="42" class="mr-3">
              <v-icon color="teal darken-2">mdi-flask-check</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold teal--text text--darken-3">
                Requiere Análisis
              </div>
              <div class="text-h6 font-weight-black teal--text text--darken-3">
                {{ totalRequiresAnalysis }}
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="grey lighten-4" size="42" class="mr-3">
              <v-icon color="grey darken-2">mdi-flask-off-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Pendientes / No Requiere
              </div>
              <div class="text-h6 font-weight-black grey--text text--darken-3">
                {{ totalNotRequiresAnalysis }}
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- 4. Tabla Principal de Proyecciones -->
    <v-card class="rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-table-large</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Proyecciones Semanales de Cosecha
        </span>
        <v-spacer></v-spacer>

        <!-- Buscador Rápido -->
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar en la grilla..."
          single-line
          hide-details
          dense
          outlined
          class="table-search-input"
          style="max-width: 280px;"
        ></v-text-field>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="tableHeaders"
        :items="filteredList"
        :search="search"
        :loading="loading"
        loading-text="Cargando proyecciones semanales..."
        no-data-text="No se encontraron registros con los filtros seleccionados"
        class="elevation-0"
        dense
        :items-per-page="15"
        :footer-props="{
          'items-per-page-options': [10, 15, 30, 50, 100],
          'items-per-page-text': 'Filas por página',
        }"
      >
        <!-- Formato ID Proyección -->
        <template v-slot:item.WptID="{ item }">
          <span class="font-weight-bold grey--text text--darken-4">#{{ item.WptID }}</span>
        </template>

        <!-- Semana -->
        <template v-slot:item.PdsID="{ item }">
          <v-chip
            small
            outlined
            class="font-weight-bold grey--text text--darken-3 border-light bg-grey-light"
          >
            Semana {{ item.PdsID || '-' }}
          </v-chip>
        </template>

        <!-- Fecha Cosecha / Prioridad de Cosecha -->
        <template v-slot:item.WptDateBegin="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar size="28" color="grey lighten-4" class="mr-2 flex-shrink-0">
              <v-icon size="16" color="teal darken-2">mdi-calendar-range</v-icon>
            </v-avatar>
            <div class="d-flex flex-column text-no-wrap" style="line-height: 1.25;">
              <span class="font-weight-bold grey--text text--darken-4" style="font-size: 0.85rem;">
                {{ formatDateDisplay(item.WptDateBegin) }}
              </span>
              <span
                v-if="formatDateDisplay(item.WptDateBegin) !== formatDateDisplay(item.WptDateEnd)"
                class="text-caption grey--text text--darken-1"
                style="font-size: 0.74rem;"
              >
                al {{ formatDateDisplay(item.WptDateEnd) }}
              </span>
              <span
                v-else
                class="text-caption grey--text text--darken-1"
                style="font-size: 0.74rem;"
              >
                (Fecha puntual)
              </span>
            </div>
          </div>
        </template>

        <!-- Productor -->
        <template v-slot:item.PrdCardName="{ item }">
          <div class="d-flex flex-column">
            <span class="font-weight-bold grey--text text--darken-4 text-truncate" style="max-width: 220px;">
              {{ item.PrdCardName || '-' }}
            </span>
            <span class="text-caption grey--text text--darken-1">
              {{ item.TypeSupplierName || 'Productor' }}
            </span>
          </div>
        </template>

        <!-- Lote y Zona -->
        <template v-slot:item.FltID="{ item }">
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-truncate" style="max-width: 140px;">
              Lote: {{ item.FltID }}
            </span>
            <span class="text-caption grey--text text--darken-1" v-if="item.ZonIDName">
              {{ item.ZonIDName }}
            </span>
          </div>
        </template>

        <!-- Cultivo y Variedad -->
        <template v-slot:item.TypeCultiveName="{ item }">
          <div class="d-flex flex-column">
            <span class="font-weight-medium">{{ item.TypeCultiveName || '-' }}</span>
            <span class="text-caption grey--text text--darken-1" v-if="item.VrtDescription">
              {{ item.VrtDescription }}
            </span>
          </div>
        </template>

        <!-- Cantidad Semana (Jabas) -->
        <template v-slot:item.CantidadSemana="{ item }">
          <span class="font-weight-bold grey--text text--darken-4">
            {{ (item.CantidadSemana || 0) | formatNumber }}
          </span>
        </template>

        <!-- Total Programado -->
        <template v-slot:item.TotalProgramado="{ item }">
          <span class="grey--text text--darken-2">
            {{ (item.TotalProgramado || 0) | formatNumber }}
          </span>
        </template>

        <!-- Chip de Estado RequiresAnalysis -->
        <template v-slot:item.RequiresAnalysis="{ item }">
          <v-chip
            v-if="item.RequiresAnalysis === 1 || item.RequiresAnalysis === '1'"
            x-small
            class="font-weight-bold"
            color="teal lighten-5"
            text-color="teal darken-3"
          >
            <v-icon left x-small color="teal darken-3">mdi-flask</v-icon>
            SÍ REQUIERE
          </v-chip>
          <v-chip
            v-else
            x-small
            class="font-weight-bold"
            color="grey lighten-4"
            text-color="grey darken-2"
          >
            <v-icon left x-small color="grey darken-2">mdi-flask-off-outline</v-icon>
            NO REQUIERE
          </v-chip>
        </template>

        <!-- Acciones: Aprobar o Cancelar -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-center">
            <!-- Si ya está aprobado: Botón para Cancelar -->
            <v-tooltip bottom v-if="item.RequiresAnalysis === 1 || item.RequiresAnalysis === '1'">
              <template v-slot:activator="{ on, attrs }">
                <span>
                  <v-btn
                    x-small
                    color="error"
                    outlined
                    class="rounded-lg text-none px-2 font-weight-bold mr-1"
                    :disabled="!isAdmin"
                    v-bind="attrs"
                    v-on="on"
                    @click="openActionModal(item, 0)"
                  >
                    <v-icon left x-small>mdi-close-circle-outline</v-icon>
                    Cancelar
                  </v-btn>
                </span>
              </template>
              <span>{{ isAdmin ? 'Cancelar requerimiento de análisis para esta proyección' : 'Se requieren permisos de administrador de menú para cancelar' }}</span>
            </v-tooltip>

            <!-- Si NO está aprobado (0 o null): Botón para Aprobar -->
            <v-tooltip bottom v-else>
              <template v-slot:activator="{ on, attrs }">
                <span>
                  <v-btn
                    x-small
                    color="teal darken-1"
                    class="white--text rounded-lg text-none px-2 font-weight-bold mr-1"
                    elevation="0"
                    :disabled="!isAdmin"
                    v-bind="attrs"
                    v-on="on"
                    @click="openActionModal(item, 1)"
                  >
                    <v-icon left x-small>mdi-check-circle-outline</v-icon>
                    Aprobar Análisis
                  </v-btn>
                </span>
              </template>
              <span>{{ isAdmin ? 'Aprobar requerimiento de análisis de materia prima' : 'Se requieren permisos de administrador de menú para aprobar' }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- 5. Modal de Confirmación para Aprobar / Cancelar Análisis -->
    <v-dialog v-model="actionDialog" max-width="500" persistent>
      <v-card class="rounded-xl border-light" outlined elevation="0">
        <v-card-title class="py-3 px-5 d-flex align-center bg-dialog-header">
          <v-icon
            :color="targetRequiresAnalysis === 1 ? 'teal darken-2' : 'error'"
            class="mr-2"
          >
            {{ targetRequiresAnalysis === 1 ? 'mdi-flask-outline' : 'mdi-alert-circle-outline' }}
          </v-icon>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-4">
            {{ targetRequiresAnalysis === 1 ? 'Aprobar Requerimiento de Análisis' : 'Cancelar Requerimiento de Análisis' }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="closeActionModal">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-5" v-if="selectedRow">
          <!-- Detalle de la proyección seleccionada -->
          <v-card class="pa-3 mb-4 rounded-lg bg-grey-light border-light" outlined elevation="0">
            <v-row dense class="text-caption">
              <v-col cols="6">
                <strong>ID Proyección:</strong> #{{ selectedRow.WptID }}
              </v-col>
              <v-col cols="6">
                <strong>Lote:</strong> {{ selectedRow.FltID }}
              </v-col>
              <v-col cols="12">
                <strong>Productor:</strong> {{ selectedRow.PrdCardName }}
              </v-col>
              <v-col cols="6">
                <strong>Campaña:</strong> {{ selectedRow.CmaName }}
              </v-col>
              <v-col cols="6">
                <strong>Semana:</strong> {{ selectedRow.PdsID }} ({{ (selectedRow.CantidadSemana || 0) | formatNumber }} jabas)
              </v-col>
              <v-col cols="12" v-if="selectedRow.WptDateBegin">
                <strong>Fecha Cosecha:</strong> {{ formatDateDisplay(selectedRow.WptDateBegin) }}
                <span v-if="formatDateDisplay(selectedRow.WptDateBegin) !== formatDateDisplay(selectedRow.WptDateEnd)">
                  al {{ formatDateDisplay(selectedRow.WptDateEnd) }}
                </span>
              </v-col>
            </v-row>
          </v-card>

          <p class="text-body-2 grey--text text--darken-3 mb-3">
            {{ targetRequiresAnalysis === 1 
              ? '¿Confirmas que este lote de proyección semanal REQUIERE análisis de materia prima?' 
              : '¿Confirmas que deseas CANCELAR el requerimiento de análisis de materia prima para esta proyección?' }}
          </p>

          <s-textarea
            label="Observación (Opcional)"
            v-model="modalObservation"
            :rows="2"
            placeholder="Ingrese una observación o motivo..."
            dense
            hide-details
          ></s-textarea>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="py-3 px-5">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="text-none rounded-lg px-4"
            @click="closeActionModal"
            :disabled="actionLoading"
          >
            Cerrar
          </v-btn>
          <v-btn
            :color="targetRequiresAnalysis === 1 ? 'teal darken-1' : 'error'"
            class="white--text text-none rounded-lg px-4 font-weight-bold"
            elevation="0"
            :loading="actionLoading"
            @click="confirmAction"
          >
            {{ targetRequiresAnalysis === 1 ? 'Sí, Aprobar' : 'Sí, Cancelar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import WeeklyProjectionService from "@/services/Technicalassistance/WeeklyProjectionService";
import SSelectVariety from "@/components/HarvestSchedule/SSelectVariety.vue";
import SSelectLot from "@/components/HarvestSchedule/SSelectLot.vue";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export default {
  name: "WeeklyProjectionAnalysisRequirement",
  components: {
    SSelectVariety,
    SSelectLot,
  },
  filters: {
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return "0";
      return Number(val).toLocaleString("es-PE");
    },
  },
  data() {
    return {
      loading: false,
      search: "",
      projectionList: [],
      years: [],
      selectedWeekObj: null,

      // Filtros del Endpoint
      filters: {
        CmaID: null,
        WptYear: new Date().getFullYear(),
        PdsID: null,
        TypeCultive: null,
        VrtID: null,
        FltID: null,
        BeginDate: null,
        EndDate: null,
      },

      // Filtro local adicional
      localStatusFilter: "ALL",
      filterStatusOptions: [
        { text: "Todos los Estados", value: "ALL" },
        { text: "Requiere Análisis (Aprobado)", value: "REQ" },
        { text: "No Requiere / Pendiente", value: "NOT_REQ" },
      ],

      // Cabeceras de la grilla
      headers: [
        // { text: "ID", value: "WptID", width: "75px", sortable: true },
        { text: "ID / ZONA", value: "FltID", width: "135px", sortable: true },
        { text: "CAMPAÑA", value: "CmaName", width: "150px", sortable: true },
        { text: "SEMANA", value: "PdsID", width: "115px", sortable: true },
        { text: "FECHA COSECHA", value: "WptDateBegin", width: "175px", sortable: true },
        { text: "PRODUCTOR", value: "PrdCardName", width: "230px", sortable: true },
        { text: "CULTIVO / VARIEDAD", value: "TypeCultiveName", width: "150px", sortable: true },
        { text: "CANT. SEMANA", value: "CantidadSemana", align: "end", width: "115px", sortable: true },
        { text: "TOTAL PROG.", value: "TotalProgramado", align: "end", width: "115px", sortable: true },
        { text: "REQUIERE ANÁLISIS", value: "RequiresAnalysis", align: "center", width: "150px", sortable: true },
        { text: "ACCIONES", value: "actions", align: "center", width: "140px", sortable: false },
      ],

      // Modal de acción (aprobar / cancelar)
      actionDialog: false,
      actionLoading: false,
      selectedRow: null,
      targetRequiresAnalysis: 1,
      modalObservation: "",
    };
  },
  computed: {
    filteredList() {
      if (this.localStatusFilter === "REQ") {
        return this.projectionList.filter(
          (x) => x.RequiresAnalysis === 1 || x.RequiresAnalysis === "1"
        );
      }
      if (this.localStatusFilter === "NOT_REQ") {
        return this.projectionList.filter(
          (x) => x.RequiresAnalysis !== 1 && x.RequiresAnalysis !== "1"
        );
      }
      return this.projectionList;
    },

    totalCantidadSemana() {
      return this.filteredList.reduce(
        (sum, item) => sum + Number(item.CantidadSemana || 0),
        0
      );
    },

    totalRequiresAnalysis() {
      return this.projectionList.filter(
        (x) => x.RequiresAnalysis === 1 || x.RequiresAnalysis === "1"
      ).length;
    },

    totalNotRequiresAnalysis() {
      return this.projectionList.filter(
        (x) => x.RequiresAnalysis !== 1 && x.RequiresAnalysis !== "1"
      ).length;
    },

    isAdmin() {
      if (!this.$fun) return false;
      const sec = typeof this.$fun.getSecurity === "function" ? this.$fun.getSecurity() : null;
      if (sec && (sec.IsLevelAdmin === true || sec.IsLevelAdmin === 1)) return true;
      if (typeof this.$fun.isAdmin === "function" && this.$fun.isAdmin()) return true;
      return false;
    },

    tableHeaders() {
      if (this.isAdmin) {
        return this.headers;
      }
      return this.headers.filter((h) => h.value !== "actions");
    },
  },
  created() {
    this.initYears();
    this.loadData();
  },
  methods: {
    initYears() {
      const rawYears = this.$fun?.getNext3Years?.() || [
        new Date().getFullYear() - 1,
        new Date().getFullYear(),
        new Date().getFullYear() + 1,
      ];
      this.years = rawYears.map((y) => {
        if (typeof y === "object" && y !== null) {
          return { text: String(y.text || y.value), value: y.value };
        }
        return { text: String(y), value: y };
      });
    },

    onWeekChange(weekObj) {
      if (!weekObj) {
        this.filters.PdsID = null;
        this.filters.BeginDate = null;
        this.filters.EndDate = null;
        return;
      }
      this.filters.PdsID = weekObj.PdsID || null;
      this.filters.BeginDate = weekObj.PdsDateBegin || null;
      this.filters.EndDate = weekObj.PdsDateEnd || null;
    },

    formatDateDisplay(val) {
      if (!val) return "-";
      if (val instanceof Date) {
        const d = String(val.getDate()).padStart(2, "0");
        const m = String(val.getMonth() + 1).padStart(2, "0");
        const y = val.getFullYear();
        return `${d}/${m}/${y}`;
      }

      const str = String(val).trim();
      // Formato ISO / guión (YYYY-MM-DD o YYYY-MM-DDT...)
      if (str.includes("-")) {
        const dateOnly = str.split("T")[0].split(" ")[0];
        const parts = dateOnly.split("-");
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            // YYYY-MM-DD
            const y = parts[0];
            const m = parts[1].padStart(2, "0");
            const d = parts[2].padStart(2, "0");
            return `${d}/${m}/${y}`;
          } else if (parts[2].length === 4) {
            // DD-MM-YYYY
            const d = parts[0].padStart(2, "0");
            const m = parts[1].padStart(2, "0");
            const y = parts[2];
            return `${d}/${m}/${y}`;
          }
        }
      }

      // Formato slash (DD/MM/YYYY o D/M/YYYY o YYYY/MM/DD)
      if (str.includes("/")) {
        const dateOnly = str.split("T")[0].split(" ")[0];
        const parts = dateOnly.split("/");
        if (parts.length === 3) {
          if (parts[2].length === 4) {
            // DD/MM/YYYY
            const d = parts[0].padStart(2, "0");
            const m = parts[1].padStart(2, "0");
            const y = parts[2];
            return `${d}/${m}/${y}`;
          } else if (parts[0].length === 4) {
            // YYYY/MM/DD
            const y = parts[0];
            const m = parts[1].padStart(2, "0");
            const d = parts[2].padStart(2, "0");
            return `${d}/${m}/${y}`;
          }
        }
      }

      return str;
    },

    formatDateShort(dateStr) {
      return this.formatDateDisplay(dateStr);
    },

    async loadData() {
      this.loading = true;
      try {
        const requestID = this.$fun?.getUserID?.() || 1;

        const payload = {
          CmaID: this.filters.CmaID ? Number(this.filters.CmaID) : null,
          PdsID: this.filters.PdsID ? Number(this.filters.PdsID) : null,
          TypeCultive: this.filters.TypeCultive ? Number(this.filters.TypeCultive) : null,
          VrtID: this.filters.VrtID ? Number(this.filters.VrtID) : null,
          FltID: this.filters.FltID ? Number(this.filters.FltID) : null,
          BeginDate: this.filters.BeginDate || null,
          EndDate: this.filters.EndDate || null,
        };

        const resp = await WeeklyProjectionService.requiresAnalysis(payload, requestID);
        this.projectionList = (resp && resp.data) || [];
      } catch (err) {
        console.error("Error al cargar proyecciones con requerimiento de análisis:", err);
        this.$fun.alert("Ocurrió un error al consultar las proyecciones.", "error");
      } finally {
        this.loading = false;
      }
    },

    openActionModal(row, targetValue) {
      this.selectedRow = row;
      this.targetRequiresAnalysis = targetValue;
      this.modalObservation = "";
      this.actionDialog = true;
    },

    closeActionModal() {
      this.actionDialog = false;
      this.selectedRow = null;
      this.modalObservation = "";
    },

    async confirmAction() {
      if (!this.selectedRow || !this.selectedRow.WptID) return;

      if (!this.isAdmin) {
        this.$fun.alert("No cuenta con permisos de administrador de menú para aprobar o cancelar el análisis.", "warning");
        return;
      }

      this.actionLoading = true;
      try {
        const requestID = this.$fun?.getUserID?.() || 1;
        const payload = {
          WptID: this.selectedRow.WptID,
          RequiresAnalysis: this.targetRequiresAnalysis,
          ObservationAnalysis: this.modalObservation ? this.modalObservation.trim() : null,
          UsrUpdateID: requestID,
        };

        const resp = await WeeklyProjectionService.updateRequiresAnalysis(payload, requestID);
        if (resp && (resp.data === true || resp.status === 200)) {
          // Actualización reactiva local
          this.$set(this.selectedRow, "RequiresAnalysis", this.targetRequiresAnalysis);
          if (this.modalObservation) {
            this.$set(this.selectedRow, "ObservationAnalysis", this.modalObservation);
          }

          const msg =
            this.targetRequiresAnalysis === 1
              ? "¡Proyección semanal aprobada para análisis!"
              : "Requerimiento de análisis cancelado correctamente.";
          this.$fun.alert(msg, "success");
          this.closeActionModal();
        } else {
          this.$fun.alert("No se pudo actualizar el estado de análisis.", "error");
        }
      } catch (err) {
        console.error("Error actualizando RequiresAnalysis:", err);
        this.$fun.alert("Error de comunicación al actualizar el análisis.", "error");
      } finally {
        this.actionLoading = false;
      }
    },

    async exportToExcel() {
      if (this.filteredList.length === 0) return;

      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Requerimiento Análisis");

        // Cabeceras Excel
        worksheet.columns = [
          { header: "ID", key: "WptID", width: 10 },
          { header: "Campaña", key: "CmaName", width: 22 },
          { header: "Semana", key: "PdsID", width: 12 },
          { header: "Fecha Inicio", key: "WptDateBegin", width: 14 },
          { header: "Fecha Fin", key: "WptDateEnd", width: 14 },
          { header: "Productor", key: "PrdCardName", width: 32 },
          { header: "Tipo Proveedor", key: "TypeSupplierName", width: 16 },
          { header: "Lote", key: "FltID", width: 12 },
          { header: "Zona", key: "ZonIDName", width: 20 },
          { header: "Cultivo", key: "TypeCultiveName", width: 16 },
          { header: "Variedad", key: "VrtDescription", width: 16 },
          { header: "Cantidad Semana", key: "CantidadSemana", width: 16 },
          { header: "Total Programado", key: "TotalProgramado", width: 16 },
          { header: "Requiere Análisis", key: "RequiresAnalysisText", width: 18 },
        ];

        // Estilo cabecera
        worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
        worksheet.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF0D9488" }, // Teal neutro
        };
        worksheet.getRow(1).alignment = { vertical: "middle", horizontal: "center" };

        // Filas
        this.filteredList.forEach((row) => {
          worksheet.addRow({
            WptID: row.WptID,
            CmaName: row.CmaName || "",
            PdsID: row.PdsID || "",
            WptDateBegin: this.formatDateDisplay(row.WptDateBegin),
            WptDateEnd: this.formatDateDisplay(row.WptDateEnd),
            PrdCardName: row.PrdCardName || "",
            TypeSupplierName: row.TypeSupplierName || "",
            FltID: row.FltID || "",
            ZonIDName: row.ZonIDName || "",
            TypeCultiveName: row.TypeCultiveName || "",
            VrtDescription: row.VrtDescription || "",
            CantidadSemana: Number(row.CantidadSemana || 0),
            TotalProgramado: Number(row.TotalProgramado || 0),
            RequiresAnalysisText:
              row.RequiresAnalysis === 1 || row.RequiresAnalysis === "1" ? "SÍ" : "NO",
          });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `Requerimiento_Analisis_Proyecciones_${new Date().toISOString().slice(0, 10)}.xlsx`);
      } catch (e) {
        console.error("Error al exportar Excel:", e);
        this.$fun.alert("Error al generar el archivo Excel.", "error");
      }
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}

.bg-white {
  background-color: #ffffff !important;
}

.bg-grey-light {
  background-color: #f8fafc !important;
}

.bg-dialog-header {
  background-color: #f8fafc !important;
}
</style>
